import React, { useState } from 'react';
import styled from 'styled-components';
import { generateClient } from 'aws-amplify/api';
import { PageContainer } from '../App';

const client = generateClient();

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      await client.models.Contact.create({
        name: formData.name,
        email: formData.email,
        message: formData.message,
        status: 'new'
      });
      
      setSuccess(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError('Failed to submit message. Please try again.');
      console.error('Error submitting contact form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <PageContainer>
      <ContactContent>
        <ContactTitle>Get in Touch</ContactTitle>
        <ContactDescription>
          Have a question or feedback? We'd love to hear from you.
        </ContactDescription>

        {success && (
          <SuccessMessage>
            Thank you for your message! We'll get back to you soon.
          </SuccessMessage>
        )}

        <ContactForm onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Name</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="message">Message</Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="5"
            />
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </SubmitButton>
        </ContactForm>
      </ContactContent>
    </PageContainer>
  );
};

const ContactContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const ContactTitle = styled.h1`
  font-size: 2.5rem;
  margin: 0 0 1rem;
  font-family: gt-super, Georgia, serif;
  color: #292929;
`;

const ContactDescription = styled.p`
  font-size: 1.2rem;
  color: #757575;
  margin-bottom: 2rem;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #292929;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: #1a8917;
  }
`;

const TextArea = styled.textarea`
  padding: 0.8rem;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  font-size: 1rem;
  outline: none;
  resize: vertical;

  &:focus {
    border-color: #1a8917;
  }
`;

const SubmitButton = styled.button`
  background: #1a8917;
  color: white;
  border: none;
  padding: 0.8rem 1.6rem;
  border-radius: 99px;
  font-size: 1rem;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    background: #156912;
  }

  &:disabled {
    background: #82c480;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e53935;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #1a8917;
  background: #e8f5e9;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
`;

export default ContactPage; 